/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { forwardRef } from 'react';
import { token } from '@atlaskit/tokens';
import * as colors from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';

import { type Organisation } from '../../types';
import { SWITCHER_TAB_BUTTON_PREFIX, SWITCHER_TAB_PANEL_PREFIX } from './utils';
import { useIsSwitcherTeam25TestCohort } from '../components/experiment-provider';
import { useThemeObserver } from '@atlaskit/tokens';

const tokenAccents = [
	[
		token('color.background.accent.red.subtlest', '#FFEDEB'),
		token('color.text.accent.red', '#AE2A19'),
	],
	[
		token('color.background.accent.blue.subtler', '#CCE0FF'),
		token('color.text.accent.blue', '#0055CC'),
	],
	[
		token('color.background.accent.green.subtler', '#BAF3DB'),
		token('color.text.accent.green', '#216E4E'),
	],
	[
		token('color.background.accent.yellow.subtler', '#F8E6A0'),
		token('color.text.accent.yellow', '#7F5F01'),
	],
	[
		token('color.background.accent.purple.subtler', '#DFD8FD'),
		token('color.text.accent.purple', '#5E4DB2'),
	],
	[
		token('color.background.accent.magenta.subtler', '#FDD0EC'),
		token('color.text.accent.magenta', '#943D73'),
	],
	[
		token('color.background.accent.teal.subtler', '#C1F0F5'),
		token('color.text.accent.teal', '#206B74'),
	],
	[
		token('color.background.accent.orange.subtler', '#FFE2BD'),
		token('color.text.accent.orange', '#974F0C'),
	],
	[
		token('color.background.accent.lime.subtler', '#D3F1A7'),
		token('color.text.accent.lime', '#4C6B1F'),
	],
	[
		token('color.background.accent.gray.subtler', '#DCDFE4'),
		token('color.text.accent.gray', '#44546F'),
	],
];

const tokenAccentsRefreshed = [
	[token('color.background.accent.purple.subtlest'), token('color.text.accent.purple.bolder')],
	[token('color.background.accent.blue.subtlest'), token('color.text.accent.blue.bolder')],
	[token('color.background.accent.lime.subtlest'), token('color.text.accent.lime.bolder')],
	[token('color.background.accent.yellow.subtlest'), token('color.text.accent.yellow.bolder')],
];

const tokenAccentsDarkModeRefreshed = [
	[token('color.background.accent.purple.subtler'), token('color.text.accent.purple.bolder')],
	[token('color.background.accent.blue.subtler'), token('color.text.accent.blue.bolder')],
	[token('color.background.accent.lime.subtler'), token('color.text.accent.lime.bolder')],
	[token('color.background.accent.yellow.subtler'), token('color.text.accent.yellow.bolder')],
];

const tokenAccentsHoveredDarkModeRefreshed = [
	[token('color.background.accent.purple.subtler.hovered')],
	[token('color.background.accent.blue.subtler.hovered')],
	[token('color.background.accent.lime.subtler.hovered')],
	[token('color.background.accent.yellow.subtler.hovered')],
];

const tokenAccentsHovered = [
	[token('color.background.accent.purple.subtlest.hovered')],
	[token('color.background.accent.blue.subtlest.hovered')],
	[token('color.background.accent.lime.subtlest.hovered')],
	[token('color.background.accent.yellow.subtlest.hovered')],
];

const getArbitraryElement = (arr: Array<string>[], seed: string) => {
	const charCodes = seed.split('').reduce((a, b, i) => {
		return a + b.charCodeAt(0);
	}, 0);

	return arr[charCodes % arr.length];
};

const getColorTokens = (
	displayName: string,
	isVisualRefreshEnabled: boolean,
	observedColorMode: string | undefined,
): { bgColorToken: string; textColorToken: string; hoverColorToken: string } => {
	let colorTokens: string[];
	let hoverColorTokens: string[];

	if (isVisualRefreshEnabled) {
		if (observedColorMode === 'dark') {
			colorTokens = getArbitraryElement(tokenAccentsDarkModeRefreshed, displayName);
			hoverColorTokens = getArbitraryElement(tokenAccentsHoveredDarkModeRefreshed, displayName);
		} else {
			colorTokens = getArbitraryElement(tokenAccentsRefreshed, displayName);
			hoverColorTokens = getArbitraryElement(tokenAccentsHovered, displayName);
		}
	} else {
		colorTokens = getArbitraryElement(tokenAccents, displayName);
		hoverColorTokens = getArbitraryElement(tokenAccentsHovered, displayName);
	}

	const [bgColorToken, textColorToken] = colorTokens;
	const [hoverColorToken] = hoverColorTokens;

	return { bgColorToken, textColorToken, hoverColorToken };
};

interface Props extends Organisation {
	isActive: boolean;
	onClick: () => void;
	onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
	tabIndex?: number;
	percentageOpen?: number;
}

const OrganisationItem = forwardRef<HTMLButtonElement, Props>(
	({ onKeyDown, displayName, isActive, onClick, tabIndex, id, percentageOpen = 1 }, ref) => {
		const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();

		const displayInitials = displayName.substring(0, 2);

		const { colorMode: observedColorMode } = useThemeObserver();
		const { bgColorToken, textColorToken, hoverColorToken } = getColorTokens(
			displayName,
			isVisualRefreshEnabled,
			observedColorMode,
		);

		const tokenColours = css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			backgroundColor: bgColorToken,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			color: textColorToken,
			'&:hover': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				backgroundColor: isVisualRefreshEnabled ? hoverColorToken : bgColorToken,
			},
		});

		return (
			<div css={[orgItemContainerStyles, isActive && activeOrgItemContainerStyles]}>
				<Tooltip content={displayName} position="right" ignoreTooltipPointerEvents>
					{({ ref: tooltipRef, 'aria-describedby': _, ...tooltipProps }) => (
						<button
							{...tooltipProps}
							role="tab"
							aria-selected={isActive ? 'true' : 'false'}
							ref={mergeRefs([ref, tooltipRef])}
							onKeyDown={onKeyDown}
							aria-label={displayName}
							id={`${SWITCHER_TAB_BUTTON_PREFIX}-${id}`}
							aria-controls={`${SWITCHER_TAB_PANEL_PREFIX}-${id}`}
							tabIndex={tabIndex}
							onClick={onClick}
							css={
								isVisualRefreshEnabled
									? orgItemTooltipContainerStylesRefreshed
									: orgItemTooltipContainerStyles
							}
						>
							<div
								css={[
									isVisualRefreshEnabled ? orgItemStylesRefreshed : orgItemStyles,
									// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
									isActive ? activeOrgItemStyles : {},
									// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
									tokenColours,
								]}
							>
								<span aria-hidden="true" css={orgItemInitialStyles}>
									{displayInitials}
								</span>
							</div>
							{percentageOpen > 0 && (
								<span
									aria-hidden="true"
									css={orgItemTextStyles}
									style={{
										opacity: percentageOpen,
									}}
									data-testid={`switcher-org-name`}
								>
									{displayName}
								</span>
							)}
						</button>
					)}
				</Tooltip>
			</div>
		);
	},
);

const orgItemContainerStyles = css({
	paddingLeft: token('space.150', '12px'),
	paddingRight: token('space.150', '12px'),
	position: 'relative',

	'&::before': {
		content: '""',
		position: 'absolute',
		left: '0px',
		top: '0px',
		bottom: '0px',
		width: '3px',
		borderTopRightRadius: token('border.radius.050', '1px'),
		borderBottomRightRadius: token('border.radius.050', '1px'),
	},

	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderColor: token('color.background.brand.bold.hovered', colors.B400),
		'&::before': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.brand.bold.hovered', colors.B400),
		},
	},

	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderColor: token('color.background.brand.bold.pressed', colors.B400),

		'&::before': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.brand.bold.pressed', colors.B400),
		},
	},
});

const activeOrgItemContainerStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderColor: token('color.background.brand.bold', colors.B400),
	'&::before': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.brand.bold', colors.B400),
	},
});

const orgItemTooltipContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	background: 'transparent',
	cursor: 'pointer',
	border: 'transparent',
	width: '100%',
	padding: '0',
	'&:hover': {
		opacity: 0.8,
	},
});

const orgItemTooltipContainerStylesRefreshed = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	background: 'transparent',
	cursor: 'pointer',
	border: 'transparent',
	width: '100%',
	padding: token('space.025', '2px'),
});

const orgItemStyles = css({
	cursor: 'pointer',
	background: 'transparent',
	padding: 0,
	boxSizing: 'content-box',
	textAlign: 'center',
	verticalAlign: 'middle',
	textTransform: 'capitalize',
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.inverse', colors.N0),
	borderRadius: token('border.radius.200', '8px'),
	border: '2px solid transparent',
	width: '28px',
	height: '28px',
	flex: 'none',
});

const orgItemStylesRefreshed = css({
	background: 'transparent',
	padding: 0,
	boxSizing: 'content-box',
	textAlign: 'center',
	verticalAlign: 'middle',
	textTransform: 'capitalize',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.inverse', colors.N0),
	borderRadius: token('border.radius.200', '8px'),
	border: '2px solid transparent',
	width: '28px',
	height: '28px',
	flex: 'none',
});

const activeOrgItemStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderColor: token('color.background.brand.bold', colors.B400),
	'&:focus-visible': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderColor: token('color.background.brand.bold.hovered', colors.B500),
		opacity: 0.8,
		outline: 0,
	},
});

const orgItemInitialStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '28px',
});

const orgItemTextStyles = css({
	margin: '0',
	font: token('font.body'),
	fontWeight: token('font.weight.medium', '500'),
	color: token('color.text'),
	display: '-webkit-box',
	overflow: 'hidden',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 1,
	wordBreak: 'break-all',
	height: '100%',
	paddingLeft: token('space.100', '8px'),
	textOverflow: 'ellipsis',
	textAlign: 'left',
	transition: 'opacity 300ms',
});
export default OrganisationItem;
